import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SKILLS } from "@/lib/skills";
import { LEVEL_FILTERS, levelLabel } from "@/lib/levels";
import { Plus, Wand2, Target } from "lucide-react";

const inp = "w-full h-10 px-3 rounded-lg border border-[#d6cecf] bg-white";
const kebab = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const matchesFocus = (g, skill, level) =>
  (!skill || (g.skill_sets || []).includes(skill)) && (!level || String(g.level).toUpperCase() === level);

export function SkillFocusPanel({ skill, setSkill, level, setLevel, games, usedIds, onAdd, onAutoBuild }) {
  const suggestions = skill ? games.filter((g) => matchesFocus(g, skill, level) && !usedIds.has(g.id)) : [];
  return (
    <div className="mt-4 bg-white rounded-2xl border border-[#dfd8da] p-6" data-testid="skill-focus-panel">
      <div className="flex items-center gap-2 text-[#550001] font-bold"><Target size={18} aria-hidden="true" /> Skill focus</div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="a-skill">Skill</Label>
          <select id="a-skill" value={skill} onChange={(e) => setSkill(e.target.value)} className={`mt-1 ${inp}`} data-testid="build-skill-select">
            <option value="">— No skill focus —</option>
            {SKILLS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="a-level">Class level</Label>
          <select id="a-level" value={level} onChange={(e) => setLevel(e.target.value)} className={`mt-1 ${inp}`} data-testid="build-level-select">
            {LEVEL_FILTERS.map(([v, l]) => <option key={v || "any"} value={v}>{v ? l : "Any level"}</option>)}
          </select>
        </div>
      </div>
      {skill && (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-[#545454]" data-testid="suggestions-count">{suggestions.length} suggested games for {skill}{level ? ` · ${levelLabel(level)}` : ""}</p>
            <Button onClick={onAutoBuild} disabled={suggestions.length === 0} className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="build-auto-btn">
              <Wand2 size={16} className="mr-1" /> Auto-build agenda
            </Button>
          </div>
          <ul className="mt-3 max-h-72 overflow-y-auto divide-y divide-[#dfd8da] border border-[#dfd8da] rounded-lg" data-testid="suggested-games">
            {suggestions.map((g) => (
              <li key={g.id} className="flex items-center gap-3 p-3" data-testid={`suggested-game-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1a1617] truncate">{g.name}</div>
                  <div className="text-xs text-[#545454] line-clamp-1">{levelLabel(g.level)} · {(g.skill_sets || []).join(", ")}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => onAdd(g)} className="border-[#920e12] text-[#920e12] shrink-0" data-testid={`add-suggested-${g.id}`}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </li>
            ))}
            {suggestions.length === 0 && <li className="p-3 text-sm text-[#545454]" data-testid={`no-suggestions-${kebab(skill)}`}>No more matching games. Try "Any level".</li>}
          </ul>
        </>
      )}
    </div>
  );
}
